import {
  LeadColumn,
  LeadData,
  LeadStatus,
  SalesExplorationStatus
} from "@/pages/leads/types";
import { APILookupValue } from "@/services/api/types";
import { TFunction } from "i18next";
import i18n from "i18next";
import { camelCase } from "lodash";

export const leadsColumnKeys = Object.values(LeadColumn);

type LeadStatusColorMap = {
  [K in LeadStatus]: string;
} & { null: string };

export const leadStatusColors: LeadStatusColorMap = {
  [LeadStatus.IN_PROGRESS]: "var(--natural)",
  [LeadStatus.STUCK]: "var(--charts-warning)",
  [LeadStatus.CLOSED_QUALIFIED]: "var(--charts-positive)",
  [LeadStatus.DISQUALIFIED]: "var(--charts-negative)",
  [LeadStatus.CONVERTED]: "var(--cta)",
  [LeadStatus.CONFIRM_ICP]: "var(--charts-warning)",
  null: "var(--warning)"
};

export const salesExplorationStatusColors: Record<
  SalesExplorationStatus,
  string
> = {
  [SalesExplorationStatus.NOT_BOOKED]: "var(--charts-negative)",
  [SalesExplorationStatus.BOOKED]: "var(--charts-warning)",
  [SalesExplorationStatus.DONE]: "var(--charts-positive)"
};

export const getOpenLeads = (leads: LeadData[]) =>
  leads.filter(
    (lead) =>
      ![LeadStatus.DISQUALIFIED, LeadStatus.CLOSED_QUALIFIED].includes(
        lead.status as LeadStatus
      )
  );

/**
 * The licenses a lead's license field can offer: its product's. Without a
 * product the field is locked, so only the lead's own license is kept, to
 * name a value saved before the product requirement.
 */
export const getLicenseTypesForLead = <
  LicenseType extends { id: string; productId: string }
>(
  licenseTypes: LicenseType[],
  lead: Pick<LeadData, "productId" | "licenseId">
) =>
  lead.productId
    ? licenseTypes.filter((license) => license.productId === lead.productId)
    : licenseTypes.filter((license) => license.id === lead.licenseId);

export const disqualifiedReasonsFromIdx = (
  t: TFunction<"translation", undefined>,
  disqualifiedReasons: APILookupValue[],
  value: number
) => {
  const reason = disqualifiedReasons.find((i) => i.id === value);
  if (reason) {
    const translationKey = `leads.modal.disqualifiedReasons.${camelCase(reason.name)}`;
    return i18n.exists(translationKey) ? t(translationKey) : reason.name;
  }
  return t("tables.notAvailable");
};

export const closedAsQualifiedReasonsFromIdx = (
  t: TFunction<"translation", undefined>,
  closedAsQualifiedReasons: APILookupValue[],
  value: number
) => {
  const reason = closedAsQualifiedReasons.find((i) => i.id === value);
  if (reason) {
    const translationKey = `leads.modal.closedAsQualifiedReasons.${camelCase(reason.name)}`;
    return i18n.exists(translationKey) ? t(translationKey) : reason.name;
  }
  return t("tables.notAvailable");
};

export const sourcesFromIdx = (
  t: TFunction<"translation", undefined>,
  sources: APILookupValue[],
  value: number
) => {
  const source = sources.find((i) => i.id === value);
  if (source) {
    return i18n.exists(`leads.sourceValues.${camelCase(source.name)}`)
      ? t(`leads.sourceValues.${camelCase(source.name)}`)
      : source.name;
  }
  return t("tables.notAvailable");
};
